/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { convertToRaw } from "draft-js";

import { DEFAULT_LANGUAGE_ARRAY } from "../../config";
import { simpleFetch } from "../../restWrapper";
import {
  normalizeDescriptionContent,
  descriptionCodeFrom,
  descriptionCodeFields,
  serializeDescriptionCodes,
} from "../../assets/projectContentHelper.js";
import TextBox from "../CreateGoal/TextBox";
import DescriptionCopyMenu from "../CreateGoal/DescriptionCopyMenu";
import "./ProjectDescriptionEditor.css";

const LNG_ARR = [...DEFAULT_LANGUAGE_ARRAY, "default"];

class ProjectDescriptionEditor extends Component {
  constructor(props) {
    super(props);
    const activeLang =
      props.language && DEFAULT_LANGUAGE_ARRAY.indexOf(props.language) !== -1
        ? props.language
        : DEFAULT_LANGUAGE_ARRAY[0];
    // The project list no longer carries descriptions (CP-229), so a mission
    // opened from the table usually arrives without one. Editing must wait for
    // the full project fetch: initializing from a missing description would
    // fill every language slot with empty content, and saving that would
    // silently wipe the real description.
    const hasDescription = !!props.mission.description;
    this.state = {
      activeLang,
      content: hasDescription
        ? normalizeDescriptionContent(
          activeLang,
          props.mission.description,
          LNG_ARR
        )
        : null,
      loading: !hasDescription,
      saving: false,
      error: "",
    };
    this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    if (this.state.content) return;
    simpleFetch("/projects/" + this.props.mission.id, "GET")
      .then((res) => {
        if (res && res.status === 200 && res.body) {
          this.setState({
            content: normalizeDescriptionContent(
              this.state.activeLang,
              res.body.description,
              LNG_ARR
            ),
            loading: false,
          });
          return;
        }
        this.setState({
          loading: false,
          error: this.t(
            "descriptionLoadFailed",
            "Could not load the description. Close and try again."
          ),
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
          error: this.t(
            "descriptionLoadFailed",
            "Could not load the description. Close and try again."
          ),
        });
      });
  }

  t = (key, fallback) => {
    const block =
      this.props.languageObj && typeof this.props.languageObj === "object"
        ? this.props.languageObj
        : {};
    return block[key] || fallback;
  };

  handleTabClick(lang) {
    this.setState({ activeLang: lang });
  }

  handleDescriptionChange(editorState) {
    const { activeLang, content } = this.state;
    this.setState({
      content: { ...content, [activeLang]: editorState },
    });
  }

  // Copy the shown language's description as bulk-import code. Uses the live
  // editor content rather than what's persisted, so unsaved tweaks are
  // included and copying never requires saving first.

  // Copy every language at once as a row of cells, in template column order,
  // so one paste into the Description (Default) cell fills all four columns.

  handleCancel() {
    this.props.onClose();
  }

  handleKeyDown(e) {
    if (e.key === "Escape") {
      this.handleCancel();
    }
  }

  handleSave() {
    const { content } = this.state;
    // Never save before the description has loaded — writing the empty
    // editor state would overwrite the real description.
    if (!content) return;
    // Languages identical to default are dropped from storage, and the
    // multi-language flag is derived from whether any distinct translation
    // remains (CP-234) — this editor has no explicit toggle.
    const codes = {};
    LNG_ARR.forEach((lng) => {
      codes[lng] = JSON.stringify(
        convertToRaw(content[lng].getCurrentContent())
      );
    });
    this.setState({ saving: true, error: "" });
    this.props
      .onSave(this.props.mission, { description: serializeDescriptionCodes(codes) })
      .then((ok) => {
        if (ok) {
          this.props.onClose();
          return;
        }
        this.setState({
          saving: false,
          error: this.t("updateFailed", "Update failed. Please try again."),
        });
      })
      .catch(() => {
        this.setState({
          saving: false,
          error: this.t("updateFailed", "Update failed. Please try again."),
        });
      });
  }

  render() {
    const { activeLang, content, saving, loading, error } = this.state;
    return (
      <div
        className="ProjectDescriptionEditor"
        onKeyDown={this.handleKeyDown}
      >
        <div className="ProjectDescriptionEditor-tabs">
          {DEFAULT_LANGUAGE_ARRAY.map((lng) => (
            <button
              key={lng}
              type="button"
              className={
                "ProjectDescriptionEditor-tab" +
                (lng === activeLang ? " ProjectDescriptionEditor-tab--active" : "")
              }
              onClick={() => this.handleTabClick(lng)}
            >
              {this.props.langLabel(lng)}
            </button>
          ))}
        </div>

        {error ? (
          <div className="ProjectDescriptionEditor-error">{error}</div>
        ) : null}

        {content ? (
          <TextBox
            descriptionContent={content[activeLang]}
            handleDescription={this.handleDescriptionChange}
            placeholder={this.t(
              "descriptionPlaceholder",
              "Write the description here..."
            )}
          />
        ) : (
          <div className="ProjectDescriptionEditor-loading">
            {loading
              ? this.t("descriptionLoading", "Loading description...")
              : null}
          </div>
        )}

        {content ? (
          <DescriptionCopyMenu
            languageObj={this.props.languageObj}
            resetKey={activeLang}
            getCode={() => descriptionCodeFrom(this.state.content[activeLang])}
            getFields={() => descriptionCodeFields(this.state.content)}
          />
        ) : null}

        <div className="ProjectTable-titleEditorActions">
          <button
            type="button"
            className="ProjectTable-linkBtn"
            onClick={this.handleCancel}
            disabled={saving}
          >
            {this.t("cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="ProjectTable-applyBtn"
            onClick={this.handleSave}
            disabled={saving || !content}
          >
            {saving ? this.t("saving", "Saving...") : this.t("save", "Save")}
          </button>
        </div>
      </div>
    );
  }
}

ProjectDescriptionEditor.propTypes = {
  mission: PropTypes.object.isRequired,
  language: PropTypes.string,
  languageObj: PropTypes.object,
  langLabel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ProjectDescriptionEditor;
